// Platform comparison data for SEO and chatbot optimization
import { FEE_RATE_MIN_PCT, FEE_RATE_MAX_PCT } from 'config/economyCopy';
export interface ComparisonSection {
  platform: string;
  status?: 'Active' | 'Closed' | 'Legacy';
  overview: string;
  pros: string[];
  cons: string[];
  bestFor?: string;
  migration?: string;
}

export const COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    platform: 'TradeMagic',
    status: 'Active',
    overview: `
      <p>TradeMagic is a peer-to-peer Magic card trading platform that uses a point-based economy where 100 points equals $1. Founded in 2025 by a PucaTrade co-founder, TradeMagic was built specifically to solve economic problems such as inflation while providing a better trading experience than traditional marketplaces.</p>
      <p>The platform charges a sustainable dynamic transaction fee and uses automated matching to connect traders who want what you have and have what you want. Unlike PucaTrade, TradeMagic implements point sinks, dynamic fees, and economic controls to prevent inflation and ensure long-term sustainability.</p>
    `,
    pros: [
      `Sustainable ${FEE_RATE_MIN_PCT}-${FEE_RATE_MAX_PCT}% dynamic fee (lower than TCGPlayer's 11-15%)`,
      'Automated trade matching saves time',
      'Point-based economy enables flexible trading',
      'Built-in trade protection and dispute resolution',
      'Learns from PucaTrade\'s mistakes with proven economic model',
      'Modern interface with collection import tools',
      'Active development with regular feature updates',
      'No haggling or spreadsheet management needed'
    ],
    cons: [
      'Newer platform with smaller user base than established competitors',
      'Cannot instantly cash out (point-based system)',
      'Still building liquidity for some niche cards'
    ],
    bestFor: 'Regular traders who want a fast & easy way to trade, automated matching, and lower fees than traditional marketplaces. Ideal for players building decks, collectors acquiring full sets, and traders building cubes.',
    migration: `
      <ol>
        <li><strong>Import your collection:</strong> Export from TCGPlayer, Deckbox, or MTGGoldfish and import to TradeMagic</li>
        <li><strong>Build your want list:</strong> Add cards you're looking for</li>
        <li><strong>Auto-Match:</strong> Let our system find trade matches automatically</li>
        <li><strong>Start trading:</strong> Ship cards safely, with protection from our admin team</li>
      </ol>
      <p>Your collection data transfers seamlessly with our import tools. Most users are fully set up within 15 minutes.</p>
    `
  },
  {
    platform: 'TCGPlayer Direct',
    status: 'Active',
    overview: `
      <p>TCGPlayer is the largest Magic card marketplace, operating as a traditional e-commerce platform where sellers list inventory and buyers purchase cards with cash. TCGPlayer Direct handles fulfillment for verified sellers, providing a streamlined buying experience.</p>
      <p>While TCGPlayer excels as a marketplace for buying and selling, it charges significant fees (11-15% for most sellers) and doesn't facilitate peer-to-peer trading or even buylists. It's fundamentally a cash-based marketplace rather than a trading platform.</p>
    `,
    pros: [
      'Largest inventory and user base in North America',
      'Instant cash-out when selling cards',
      'TCGPlayer Direct offers reliable fulfillment',
      'Well-established with strong buyer protection',
      'Comprehensive price data and market insights',
      'Mobile app for easy browsing and purchasing'
    ],
    cons: [
      'High fees: 11-15% for most sellers, higher for low-volume',
      'Not designed for trading (cash transactions only)',
      'Fees eat significantly into profits for sellers',
      'No buy-list or barter system',
      'Competitive marketplace favors high-volume sellers',
      'Cannot build collection through trading'
    ],
    bestFor: 'Sellers and stores who need instant cash-out and buyers who want the largest inventory selection. Not ideal for traders who want to avoid high fees or build collections through trading.',
    migration: `
      <ol>
        <li><strong>Export your TCGPlayer collection:</strong> Download CSV from TCGPlayer account</li>
        <li><strong>Import to TradeMagic:</strong> Use our TCGPlayer import tool</li>
        <li><strong>List cards for trade:</strong> Cards you'd sell on TCGPlayer can be traded on TradeMagic</li>
        <li><strong>Save on fees:</strong> Keep 95% of value vs. 85-89% on TCGPlayer</li>
      </ol>
      <p>Many users maintain both platforms: sell high-end cards on TCGPlayer for cash, trade bulk and staples on TradeMagic to avoid fees.</p>
    `
  },
  {
    platform: 'Cardsphere',
    status: 'Active',
    overview: `
      <p>Cardsphere is a peer-to-peer Magic card trading platform similar to TradeMagic and the spiritual successor to PucaTrade. The platform uses a wallet system where users buy and sell to each other. Cardsphere charges 3% per trade plus a 10% withdrawal fee.</p>
      <p>Established in 2017 after PucaTrade's decline, Cardsphere has built a loyal user base and proven its economic model works. However, the platform has seen slower development in recent years with fewer new features and modernization updates.</p>
    `,
    pros: [
      'Medium 3% transaction fee (plus 10% on withdrawal)',
      'Established platform with proven track record',
      'Active user base with good liquidity',
      'Reliable trade protection and resolution',
    ],
    cons: [
      'Older interface with fewer modern features',
      'Slower development cycle with infrequent updates',
      'Sellers set prices, with "race to the bottom" mentality',
      'Less automated than TradeMagic (more manual work)',
      'Limited collection management tools',
      'No bulk import from other platforms'     
    ],
    bestFor: 'Active traders who are comfortable with an older, established platform. Best for financially-minded traders with deep knowledge of card prices, and users who don\'t need the latest features and prefer a proven system.',
    migration: `
      <p><strong>Note:</strong> Our import tools make it easy to use both Cardsphere and TradeMagic for those that don't want to migrate completely.</p>
      <p>If you do want to consolidate to TradeMagic:</p>
      <ol>
        <li><strong>Export from Cardsphere:</strong> Download your collection and want list</li>
        <li><strong>Import to TradeMagic:</strong> Use CSV import tool</li>
        <li><strong>Cash out Cardsphere credits:</strong> Complete pending trades or sell credits</li>
        <li><strong>Enjoy better tools:</strong> Auto-match, modern UI, and active development</li>
      </ol>
    `
  },
  {
    platform: 'Facebook Groups',
    status: 'Active',
    overview: `
      <p>Magic card trading happens organically in Facebook groups like "MTG Sick Deals," "EDH/Commander Trading," and hundreds of local/regional trading groups. These groups operate without centralized platforms, relying on direct communication between traders and community trust.</p>
      <p>Facebook trading is completely free with no platform fees, but comes with significant friction, scam risk, and time investment. There's no automated matching, trade protection, or standardized pricing—everything is negotiated manually.</p>
    `,
    pros: [
      'Completely free—no platform fees',
      'Direct communication with trading partners',
      'Often faster for local in-person trades',
      'Flexible negotiation and bundling',
      'Works well for unique or unusual trades',
      'Large existing communities'
    ],
    cons: [
      'No trade protection or dispute resolution',
      'High scam risk without verification',
      'Time-consuming manual negotiation',
      'No standardized pricing (requires research)',
      'Must manage spreadsheets or lists manually',
      'Finding trade partners is inefficient',
      'Quality control issues (condition disputes)',
      'Difficult to build reputation cross-group'
    ],
    bestFor: 'Casual traders who trade infrequently, prefer personal interaction, and don\'t mind extra work. Good for local trading communities and unique one-off trades. Not recommended for regular traders who value their time.',
    migration: `
      <ol>
        <li><strong>Import your spreadsheet:</strong> Most Facebook traders maintain collection spreadsheets—import these to TradeMagic</li>
        <li><strong>Upload want lists:</strong> Stop posting "ISO" threads—let Auto-Match find traders for you</li>
        <li><strong>Gain protection:</strong> Trade with built-in dispute resolution instead of relying on admin intervention</li>
        <li><strong>Save time:</strong> No more coordinating trades across dozens of messages</li>
      </ol>
      <p>Many users maintain Facebook for local in-person trading while using TradeMagic for remote trading with better protection.</p>
    `
  },
  {
    platform: 'Deckbox',
    status: 'Active',
    overview: `
      <p>Deckbox is primarily a collection management and deck-building tool with trading features built on top. Users maintain collections and wishlists, then browse other users' profiles to propose direct trades. The platform has been around since 2010 and has a loyal user base who value its collection tracking tools.</p>
      <p>While Deckbox excels at collection management, its trading system is basic and manual. There's no point economy, automated matching, or standardized trade flow. Users must manually search for trading partners, negotiate trades individually, and manage everything through direct messages.</p>
    `,
    pros: [
      'Excellent collection management and deck-building tools',
      'Long-established platform with dedicated users',
      'Free for basic features',
      'Good for cataloging collections',
      'Trade history tracking',
      'No transaction fees'
    ],
    cons: [
      'Extremely manual trading process (no automation)',
      'No point-based economy or asynchronous trading',
      'Limited trade protection',
      'Must manually search for trading partners',
      'Time-consuming to propose and negotiate trades',
      'Older interface with limited mobile support',
      'Development has stagnated in recent years'
    ],
    bestFor: 'Users who primarily want collection management tools and only trade occasionally with specific individuals. Not ideal for active traders who want efficiency.',
    migration: `
      <ol>
        <li><strong>Export from Deckbox:</strong> Download your collection CSV from Deckbox</li>
        <li><strong>Import to TradeMagic:</strong> Use our Deckbox import tool (maintains card details)</li>
        <li><strong>Keep Deckbox for deck-building:</strong> Many users use both platforms</li>
        <li><strong>Use TradeMagic for trading:</strong> Automated matching beats manual searching</li>
      </ol>
      <p><strong>Pro tip:</strong> Use Deckbox's excellent deck-building tools to identify cards you need, then use TradeMagic's Auto-Match to actually acquire them efficiently.</p>
    `
  },
  {
    platform: 'PucaTrade',
    status: 'Closed',
    overview: `
      <p>PucaTrade was a pioneering point-based Magic card trading platform founded in 2014 that facilitated over $35 million in trades before ceasing operations in 2020. The platform introduced the concept of asynchronous trading using points, allowing users to send cards they didn't need and receive cards they wanted without requiring direct 1-for-1 matches.</p>
      <p>PucaTrade closed in part due to economic issues: inflationary point creation without corresponding sinks and lack of automated stabilization mechanisms. TradeMagic was founded by a PucaTrade co-founder specifically to solve these economic problems.</p>
    `,
    pros: [
      'Pioneered point-based asynchronous trading',
      'Made trading accessible to hudreds of thousands of users',
      'Proved the viability of point-based economies (when managed properly)',
      'Built strong community and social features',
      'Validated automated matching concepts'
    ],
    cons: [
      'Failed to implement point sinks or stabilization',
      'Ceased operations in 2020'
    ],
    bestFor: 'Historical reference only—PucaTrade no longer operates. The platform\'s economic lessons directly informed TradeMagic\'s design.',
    migration: `
      <p><strong>For former PucaTrade users:</strong></p>
      <p>TradeMagic was built by a PucaTrade co-founder who learned from the platform's mistakes. If you used PucaTrade, you'll find TradeMagic familiar but with critical improvements:</p>
      <ul>
        <li><strong>Sustainable economics:</strong> Point sinks, dynamic fees, and economic controls prevent inflation</li>
        <li><strong>Modern tools:</strong> Better automation, collection management, and matching</li>
        <li><strong>Transparency:</strong> Clear economic policies and regular reporting</li>
      </ul>
      <p>Simply create a TradeMagic account and import your collection. The familiar point-based system works the same way, but with the economic stability PucaTrade lacked.</p>
    `
  }
];

// Generate structured data schema for comparison page
export const generateComparisonSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Magic Card Trading Platform Comparison - TradeMagic vs Competitors',
    description: 'Compare TradeMagic to TCGPlayer, Cardsphere, Facebook Groups, PucaTrade, and Deckbox. See fees, features, and find the best Magic card trading platform.',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: COMPARISON_SECTIONS.map((section, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: section.platform,
          description: section.overview.replace(/<[^>]*>/g, '').substring(0, 200),
          aggregateRating: section.platform === 'TradeMagic' ? {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '234',
            bestRating: '5',
            worstRating: '1'
          } : undefined
        }
      }))
    }
  };
};
